import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useDispatch, useSelector } from 'react-redux'

dayjs.extend(utc)
import { Onboarding } from 'src/interfaces/onboarding'
import { createAsyncAction } from 'src/utils/reduxUtils'
import {
  FetchOnboarding,
  FetchOnboardings,
  ExportOnboardingsCSV,
  FetchOnboardingsCounts,
} from 'src/constants/actionTypes'
import {
  onboardingPageSizeOptions,
  onboardingInitialSortFields,
  onboardingColumns,
} from 'src/constants/onboarding/onboardingGrid'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { UserPermission } from 'src/constants/user'
import { IOnboardingsTableContext, OnboardingsTableContext } from './OnboardingsTable.context'
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'
import { getPartnerId } from 'src/selectors/user'
import { getSelectedMarket } from 'src/selectors/market'
import { OnboardingsTableFields, OnboardingStatus } from 'src/constants/onboarding/enums'
import { onboardingTableHeaderTitles } from 'src/constants/onboarding/constants'
import { OnboardingsHeader } from '../grid/OnboardingsHeader'
import { ContractorDetailsModal } from 'src/components/contractor-details/ContractorDetailsModal'
import { OnboardingsItemActionMenu } from '../grid/action/OnboardingsItemActionMenu'
import { ContractorDetails } from 'src/interfaces/contractor'
import { handleApiError } from 'src/utils/errorHandlers'
import { Pagination } from 'src/interfaces/table'
import { OnboardingsContext } from '../onboardings/Onboardings.context'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { EmptyOnboardings } from '../grid/EmptyOnboardings'
import { useGridColumnPreferences } from 'src/utils/hooks/grid/useGridColumnPreferences'
import { ExportDataModal } from 'src/components/shared/grid/export-data-modal/ExportDataModal'
import { downloadFileFromBuffer } from 'src/utils/fileDownload'

interface FetchOnboardingsParams {
  limit?: number
  offset?: number
  selectedStatus: OnboardingStatus
  searchQuery: string
  tagsFilter: string[]
  invitedAfter?: string
  invitedBefore?: string
  sortModel?: GridSortModel
}

interface OnboardingsTableProps {
  selectedStatus: OnboardingStatus
}

const getRowId = (row: Onboarding) => row.id

const LOCKED_COLUMNS = ['contractorId']

const EXPORT_FILTER_DATE_FORMAT = 'MMM D, YYYY'

const OnboardingsTableComponent = ({ selectedStatus }: OnboardingsTableProps) => {
  const dispatch = useDispatch()

  const partnerId = useSelector(getPartnerId)
  const selectedMarket = useSelector(getSelectedMarket)

  const { searchQuery, tagsFilter, dateRange } = useContext(OnboardingsContext)

  const [invitedAfter, invitedBefore] = useMemo(() => {
    if (selectedStatus === OnboardingStatus.Failed) {
      return [undefined, undefined]
    }

    const [start, end] = dateRange

    return [
      start ? dayjs.utc(start.format('YYYY-MM-DD')).startOf('day').toISOString() : undefined,
      end ? dayjs.utc(end.format('YYYY-MM-DD')).endOf('day').toISOString() : undefined,
    ]
  }, [dateRange, selectedStatus])

  const { paginationModel, limit, offset, setPaginationModel } =
    useGridPagination(onboardingPageSizeOptions)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [onboardings, setOnboardings] = useState<Array<Onboarding>>([])
  const [actionAnchor, setActionAnchor] = useState<null | HTMLElement>(null)
  const [onboardingToAction, setOnboardingToAction] = useState<Onboarding | null>(null)
  const [selectedOnboardingId, setSelectedOnboardingId] = useState<string | null>(null)
  const [contractorDetails, setContractorDetails] = useState<ContractorDetails | null>(null)
  const [counts, setCounts] = useState<number>(0)
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false)
  const [sortModel, setSortModel] = useState<GridSortModel>(
    onboardingInitialSortFields[selectedStatus]
      ? [
          {
            field: onboardingInitialSortFields[selectedStatus] || 'invitedAt',
            sort: 'desc',
          },
        ]
      : [],
  )

  const handleSortModelChange = (newModel: GridSortModel) => {
    setSortModel(newModel)
  }

  const fetchRequestRef = useRef(0)

  const fetchOnboardings = useCallback(
    async ({
      limit,
      offset,
      searchQuery,
      tagsFilter,
      invitedAfter,
      invitedBefore,
      sortModel,
      selectedStatus,
    }: FetchOnboardingsParams) => {
      if (!partnerId) {
        return
      }

      const requestId = ++fetchRequestRef.current

      setIsLoading(true)

      try {
        const params: any = {
          partnerId,
          limit,
          offset,
          onboardingStatus: selectedStatus,
        }

        if (searchQuery) {
          params.partnerDriverSearch = searchQuery
        }

        if (selectedMarket?.id) {
          params.partnerMarketId = selectedMarket?.id
        }

        if (tagsFilter?.length) {
          params.tags = tagsFilter
        }

        if (invitedAfter) {
          params.invitedAfter = invitedAfter
        }

        if (invitedBefore) {
          params.invitedBefore = invitedBefore
        }

        if (sortModel && sortModel[0]) {
          params.sort = `${sortModel[0].field}:${sortModel[0].sort?.toUpperCase()}`
        }

        const payload: { data: Array<Onboarding>; pagination: Pagination } =
          await createAsyncAction(dispatch, FetchOnboardings.request(params))

        // A newer request has started — discard this stale result.
        if (fetchRequestRef.current !== requestId) {
          return
        }

        setOnboardings(payload.data)
        setCounts(payload.pagination.total)
      } catch (err: unknown) {
        if (fetchRequestRef.current === requestId) {
          handleApiError(err)
        }
      }

      if (fetchRequestRef.current === requestId) {
        setIsLoading(false)
      }
    },
    [partnerId, selectedMarket?.id],
  )

  const fetchOnboardingCounts = useCallback(() => {
    if (!partnerId) {
      return
    }

    const params: any = { partnerId }

    if (selectedMarket?.id) {
      params.partnerMarketId = selectedMarket?.id
    }

    dispatch(FetchOnboardingsCounts.request(params))
  }, [partnerId, selectedMarket?.id])

  const refreshOnboardings = useCallback(async () => {
    await fetchOnboardings({
      limit,
      offset,
      searchQuery,
      tagsFilter,
      invitedAfter,
      invitedBefore,
      sortModel,
      selectedStatus,
    })
    fetchOnboardingCounts()
  }, [
    limit,
    offset,
    searchQuery,
    tagsFilter,
    invitedAfter,
    invitedBefore,
    sortModel,
    selectedStatus,
    fetchOnboardings,
    fetchOnboardingCounts,
  ])

  useEffect(() => {
    fetchOnboardings({
      limit,
      offset,
      searchQuery,
      tagsFilter,
      invitedAfter,
      invitedBefore,
      sortModel,
      selectedStatus,
    })
  }, [
    limit,
    offset,
    partnerId,
    selectedMarket?.id,
    searchQuery,
    tagsFilter,
    invitedAfter,
    invitedBefore,
    sortModel,
    selectedStatus,
  ])

  const handleChangePagination = useCallback((paginationModel: GridPaginationModel) => {
    setPaginationModel(paginationModel)
  }, [])

  const openOnboardingsItemAction = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, onboardingToEdit: Onboarding) => {
      setActionAnchor(event.currentTarget)
      setOnboardingToAction(onboardingToEdit)
    },
    [],
  )

  const closeOnboardingsItemAction = useCallback(() => {
    setActionAnchor(null)
    setOnboardingToAction(null)
  }, [])

  const onboardingsContextValue = useMemo(() => {
    return {
      refreshOnboardings,

      actionAnchor,
      onboardingToAction,
      openOnboardingsItemAction,
      closeOnboardingsItemAction,
    }
  }, [
    refreshOnboardings,

    actionAnchor,
    onboardingToAction,
    openOnboardingsItemAction,
    closeOnboardingsItemAction,
  ])

  const handleRowClick = useCallback(({ row }: { row: Onboarding }) => {
    setSelectedOnboardingId(row.id)
  }, [])

  const onboardingDetailsRequestRef = useRef(0)

  const handleCloseDetails = useCallback(() => {
    onboardingDetailsRequestRef.current += 1
    setSelectedOnboardingId(null)
  }, [])

  const fetchContractorDetails = useCallback(async (id: string) => {
    const requestId = ++onboardingDetailsRequestRef.current

    try {
      const contractor: ContractorDetails = await createAsyncAction(
        dispatch,
        FetchOnboarding.request({ id }),
      )

      if (onboardingDetailsRequestRef.current === requestId) {
        setContractorDetails(contractor)
      }
    } catch (err) {
      if (onboardingDetailsRequestRef.current === requestId) {
        handleApiError(err)
      }
    }
  }, [])

  useEffect(() => {
    if (!selectedOnboardingId) {
      onboardingDetailsRequestRef.current += 1
      setContractorDetails(null)
    } else {
      fetchContractorDetails(selectedOnboardingId)
    }
  }, [selectedOnboardingId])

  const refreshOnboardingDetails = useCallback(async () => {
    if (selectedOnboardingId) {
      await fetchContractorDetails(selectedOnboardingId)
    }

    refreshOnboardings()
  }, [selectedOnboardingId, refreshOnboardings])

  const { hasPermission } = useCheckPermissions()

  const columns = useMemo(() => {
    const cols = onboardingColumns[selectedStatus]

    if (hasPermission(UserPermission.TagsView)) {
      return cols
    }

    return cols.filter((col) => col.field !== 'tags')
  }, [selectedStatus, hasPermission])

  const {
    columnVisibilityModel,
    pinnedColumns,
    orderedColumns,
    onColumnVisibilityModelChange,
    onPinnedColumnsChange,
    onColumnOrderChange,
    onToggleColumnVisibility,
    onPinColumn,
    onUnpinColumn,
  } = useGridColumnPreferences({
    tableKey: `onboardings-${selectedStatus}`,
    columns,
    lockedColumns: LOCKED_COLUMNS,
  })

  const exportColumns = useMemo(() => {
    return orderedColumns
      .filter((column) => {
        return column.field !== 'actions' && columnVisibilityModel[column.field] !== false
      })
      .map((column) => {
        return { field: column.field, headerName: column.headerName || column.field }
      })
  }, [orderedColumns, columnVisibilityModel])

  const exportFilters = useMemo(() => {
    const filters: Array<{ label: string; value: string }> = []

    if (searchQuery) {
      filters.push({ label: 'Search', value: searchQuery })
    }

    if (tagsFilter?.length) {
      filters.push({ label: 'Tags', value: tagsFilter.join(', ') })
    }

    if (invitedAfter) {
      filters.push({
        label: 'Invited at from',
        value: dayjs.utc(invitedAfter).format(EXPORT_FILTER_DATE_FORMAT),
      })
    }

    if (invitedBefore) {
      filters.push({
        label: 'Invited at to',
        value: dayjs.utc(invitedBefore).format(EXPORT_FILTER_DATE_FORMAT),
      })
    }

    return filters
  }, [searchQuery, tagsFilter, invitedAfter, invitedBefore])

  const handleOpenExportModal = useCallback(() => {
    setIsExportModalOpen(true)
  }, [])

  const handleCloseExportModal = useCallback(() => {
    setIsExportModalOpen(false)
  }, [])

  const handleExportOnboardings = useCallback(
    async (count: number) => {
      if (!partnerId) {
        return
      }

      const exportColumnFields = exportColumns.map((column) => {
        return column.field
      })

      const params: any = {
        limit: count,
        onboardingStatus: selectedStatus,
        exportColumns: exportColumnFields.map((_field) => {
          const field = _field as OnboardingsTableFields

          return {
            header: onboardingTableHeaderTitles[field] || field,
            key: field,
          }
        }),
      }

      if (searchQuery) {
        params.partnerDriverSearch = searchQuery
      }

      if (selectedMarket?.id) {
        params.partnerMarketId = selectedMarket?.id
      }

      if (tagsFilter?.length) {
        params.tags = tagsFilter
      }

      if (sortModel && sortModel[0]) {
        params.sort = `${sortModel[0].field}:${sortModel[0].sort?.toUpperCase()}`
      }

      if (invitedAfter) {
        params.invitedAfter = invitedAfter
      }

      if (invitedBefore) {
        params.invitedBefore = invitedBefore
      }

      const buffer: ArrayBuffer = await createAsyncAction(
        dispatch,
        ExportOnboardingsCSV.request(params),
      )

      if (!buffer || buffer.byteLength === 0) {
        throw new Error('Empty export buffer')
      }

      downloadFileFromBuffer(buffer, 'onboarding.csv', 'text/csv')
    },
    [
      partnerId,
      selectedStatus,
      exportColumns,
      searchQuery,
      selectedMarket?.id,
      tagsFilter,
      sortModel,
      invitedAfter,
      invitedBefore,
      dispatch,
    ],
  )

  const contextValue = useMemo<IOnboardingsTableContext>(() => {
    return {
      ...onboardingsContextValue,
      columnsConfig: columns,
      columnVisibilityModel,
      pinnedColumns,
      lockedColumns: LOCKED_COLUMNS,
      onToggleColumnVisibility,
      onColumnVisibilityModelChange,
      onPinColumn,
      onUnpinColumn,
    }
  }, [
    onboardingsContextValue,
    columns,
    columnVisibilityModel,
    pinnedColumns,
    onToggleColumnVisibility,
    onColumnVisibilityModelChange,
    onPinColumn,
    onUnpinColumn,
  ])

  return (
    <OnboardingsTableContext.Provider value={contextValue}>
      <OnboardingsHeader selectedStatus={selectedStatus} />
      <GridTable<Onboarding>
        rows={onboardings}
        columns={orderedColumns}
        isLoading={isLoading}
        paginationModel={paginationModel}
        pageSizeOptions={onboardingPageSizeOptions}
        rowCount={counts}
        sortModel={sortModel}
        columnVisibilityModel={columnVisibilityModel}
        pinnedColumns={pinnedColumns}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        onPinnedColumnsChange={onPinnedColumnsChange}
        onColumnOrderChange={onColumnOrderChange}
        onSortModelChange={handleSortModelChange}
        onPaginationModelChange={handleChangePagination}
        onRowClick={handleRowClick}
        getRowId={getRowId}
        EmptyElement={EmptyOnboardings}
        tableDataTestId="contractors-onboarding-list-table"
        rowDataTestIdPrefix="contractors-onboarding-list"
        onExportClick={handleOpenExportModal}
      />
      <OnboardingsItemActionMenu />
      {contractorDetails && (
        <ContractorDetailsModal
          key={contractorDetails.id}
          contractorDetails={contractorDetails}
          flow="onboardings"
          onClose={handleCloseDetails}
          onRefresh={refreshOnboardingDetails}
        />
      )}
      <ExportDataModal
        isOpen={isExportModalOpen}
        onClose={handleCloseExportModal}
        entityNamePlural="onboarding records"
        columns={exportColumns}
        filters={exportFilters}
        defaultCount={Math.min(counts, limit)}
        maxCount={counts}
        onExport={handleExportOnboardings}
      />
    </OnboardingsTableContext.Provider>
  )
}

export const OnboardingsTable = memo(OnboardingsTableComponent)
